import { useIntl } from '@finn/ui-utils';
import { useMemo } from 'react';

import {
  FilterKey,
  FiltersResponse,
  parseFiltersResponse,
} from '../../../core';
import { FiltersState, useFiltersResponse } from '../../../filters-management';
import { FilterData, TestProps } from '../../helpers';
import { useTrackFilterCategoryClicked } from '../../tracking';
import { MultiSelectFilter } from '../atoms/MultiSelectFilter';
import { FilterOptionClickFunction } from '../grouped-filters/FiltersGroup';

type Props = TestProps & {
  title: string;
  filterKey: FilterKey;
  selectedFilters: FiltersState;
  availableFilters: FiltersResponse;
  onOptionClick: FilterOptionClickFunction;
};

export const ConfigFilter = ({
  title,
  filterKey,
  selectedFilters,
  availableFilters,
  onOptionClick,
  testId,
}: Props) => {
  const i18n = useIntl();
  const filtersResponse = useFiltersResponse();

  const parsedFiltersResponse = parseFiltersResponse(
    filterKey,
    filtersResponse,
    i18n
  );

  const trackFilterOpen = useTrackFilterCategoryClicked(filterKey);

  const selectedValues = selectedFilters[filterKey as keyof FiltersState];

  const data = useMemo(
    () => ({
      filterKey,
      selectedValues,
      availableFilterValues: parsedFiltersResponse.map((configItem) => {
        const availableFiltersByKey =
          availableFilters[filterKey as keyof FiltersResponse];

        return {
          value:
            typeof configItem !== 'string' && 'value' in configItem
              ? Number(configItem?.value)
              : configItem,
          label:
            typeof configItem !== 'string' && 'label' in configItem
              ? configItem?.label
              : configItem,
          selected: Array.isArray(selectedValues)
            ? (
                selectedValues as (string | number | FiltersResponse)[]
              ).includes(
                typeof configItem !== 'string' && 'value' in configItem
                  ? Number(configItem?.value)
                  : configItem
              )
            : undefined,
          disabled: !(Array.isArray(availableFiltersByKey)
            ? (
                availableFiltersByKey as (string | number | FiltersResponse)[]
              ).includes(
                typeof configItem !== 'string' && 'value' in configItem
                  ? Number(configItem?.value)
                  : configItem
              )
            : undefined),
        };
      }),
    }),
    [
      filtersResponse[filterKey as keyof FiltersResponse],
      availableFilters[filterKey as keyof FiltersResponse],
      selectedValues,
    ]
  );

  return (
    <MultiSelectFilter
      title={title}
      data={data as FilterData}
      onOptionClick={onOptionClick}
      onOpen={trackFilterOpen}
      testId={testId}
    />
  );
};
